import { z } from 'zod';
import { apiFor } from '@/api/client';

/*
 * Runtime contract for the Albert Heijn campaign dashboard payload.
 * Mirrors the design handoff's `DASH_DATA` shape (data.js). Distinct from the
 * Carrefour World Cup schema — this is a separate report on a separate client.
 *
 * `days` are ISO date strings (the API serialises dates as ISO); the charts
 * parse them with `new Date(...)`. Trend arrays align index-for-index with
 * `days`. Money/time-ish fields the backend can't compute numerically
 * (avgTime "3:28", totalTime "23,840 hr") are pre-formatted strings.
 */

const tierSchema = z.enum(['premium', 'gold', 'silver', 'bronze']);
// 1 | 2 | 'both' | 'w25' (single-week run, e.g. Old Amsterdam).
const periodSchema = z.union([z.literal(1), z.literal(2), z.literal('both'), z.literal('w25')]);

// A nullable number that may be absent (premium/bronze venues don't carry the
// gold/silver game metrics, and gold/silver don't carry a `details` blob).
const optNum = z.number().nullable().optional();

const venueSchema = z.object({
  id: z.string(),
  name: z.string(),
  tier: tierSchema,
  period: periodSchema,
  color: z.string(),
  game: z.string().nullable(),
  visits: z.number(),
  avgTime: z.string(),
  totalTime: z.string(),
  // v2 structured per-venue metrics (gold/silver tables + drawer). Null where a
  // metric has no source for that venue/tier (rendered as "—").
  uniqueUsers: optNum, // ≈ opt-ins / reach for that brand
  uniquePlayers: optNum, // distinct users who actually played
  optins: optNum,
  gamesPlayed: optNum,
  practiceStarts: optNum,
  completionRate: optNum, // percentage (0–100)
  digitalMissions: optNum,
  txMissions: optNum,
  leaderboard: optNum,
  vouchersGiven: optNum, // coupons distributed
  vouchersRedeemed: optNum,
  // Premium (AH/KNVB custom view) + bronze keep a free-form details map for the
  // drawer; values are numbers, pre-formatted strings ("61%"), or null.
  details: z.record(z.string(), z.union([z.number(), z.string(), z.null()])).optional(),
});

const totalsSchema = z.object({
  registrations: z.number(),
  uniqueVisitors: z.number(),
  // Distinct users who actually played a game, all-time. Optional — older
  // payloads (prod before the redeploy) don't carry it; the widget shows 0.
  uniquePlayers: z.number().optional(),
  gameplays: z.number(),
  missions: z.number(),
  activeUsers: z.number(),
  avgSessions: z.number(),
  avgGamesPerUser: z.number(),
  activeDaysPerUser: z.number(),
  presentationTime: z.string(),
  avgPresentationPerUser: z.string(),
  // Mean session duration (total time ÷ visit-sessions). Optional — payloads
  // generated before this field was added don't carry it; the KPI hides then.
  avgSessionDuration: z.string().optional(),
  couponsIssued: z.number(),
  couponsRedeemed: z.number(),
  bothGoldAndSilver: z.number(),
  uniqueVisits: z.number(), // total sessions of unique visitors
  practiceGames: z.number(), // practice/free games started over all venues
});

const periodMetaSchema = z.object({
  label: z.string(),
  start: z.string(),
  end: z.string(),
  days: z.tuple([z.number(), z.number()]),
});

export const ahPayloadSchema = z.object({
  days: z.array(z.string()), // ISO date strings
  registrations: z.array(z.number()),
  uniqueVisitors: z.array(z.number()),
  gameplays: z.array(z.number()),
  missions: z.array(z.number()),
  couponsRedeemed: z.array(z.number()),
  // Real coupons issued/redeemed per day (DocDB prize.createdDate). Its own
  // date axis — the data can predate the campaign window, so it does NOT align
  // index-for-index with `days` (unlike the trend arrays above).
  couponsByDay: z.object({
    days: z.array(z.string()),
    issued: z.array(z.number()),
    redeemed: z.array(z.number()),
  }),
  venues: z.array(venueSchema),
  venueOverlap: z.array(z.object({ label: z.string(), pct: z.number() })),
  // Cross-venue headlines (computed from analytics events).
  crossVenue: z.object({ goldAndSilverPct: z.number(), avgVenuesPerUser: z.number() }),
  menuOpens: z.object({
    total: z.number(),
    breakdown: z.array(z.object({ label: z.string(), count: z.number() })),
  }),
  totals: totalsSchema,
  periods: z.object({ p1: periodMetaSchema, p2: periodMetaSchema }),
  campaign: z.object({
    name: z.string(),
    start: z.string(),
    end: z.string(),
    lastUpdated: z.string(),
    currentDay: z.number(),
    totalDays: z.number(),
    currentPeriod: z.number(),
  }),
});

export const ahEnvelopeSchema = z.object({
  data: ahPayloadSchema,
  meta: z
    .object({
      report_id: z.string().optional(),
      title: z.string().optional(),
      last_refreshed_at: z.string().nullable().optional(),
    })
    .optional(),
});

export type AhVenue = z.infer<typeof venueSchema>;
export type AhTier = z.infer<typeof tierSchema>;
export type AhPeriod = z.infer<typeof periodSchema>;
export type AhTotals = z.infer<typeof totalsSchema>;
export type AhPayload = z.infer<typeof ahPayloadSchema>;
export type AhEnvelope = z.infer<typeof ahEnvelopeSchema>;

/** Fetch + validate the AH report from its backend (envelope `{ data, meta }`). */
export async function getAhReport(reportId: string, baseUrl: string): Promise<AhEnvelope> {
  const raw = await apiFor(baseUrl).get(`reports/${reportId}`).json();
  return ahEnvelopeSchema.parse(raw);
}
